import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import './index.css';
import PrivateRoute from './components/PrivateRoute';
import SessionInvalidationBridge from './components/SessionInvalidationBridge';
import LoginPage from './pages/LoginPage';
import OtpPage from './pages/OtpPage';
import PortalLayout from './pages/portal/PortalLayout';
import PortalWelcomePage from './pages/portal/PortalWelcomePage';
import PortalHomePage from './pages/portal/PortalHomePage';
import PortalProfilePage from './pages/portal/PortalProfilePage';
import PortalPledgesPage from './pages/portal/PortalPledgesPage';
import PortalPledgePayPage from './pages/portal/PortalPledgePayPage';
import PortalPledgePaymentReceiptPage from './pages/portal/PortalPledgePaymentReceiptPage';
import PortalDonationsPage from './pages/portal/PortalDonationsPage';
import PortalStatementsPage from './pages/portal/PortalStatementsPage';
import PublicDonateLayout from './pages/public/PublicDonateLayout';
import PublicDonateLinkPage from './pages/public/PublicDonateLinkPage';
import PublicDonateCatalogPage from './pages/public/PublicDonateCatalogPage';
import PublicDonateCartPage from './pages/public/PublicDonateCartPage';
import PublicDonateCheckoutPage from './pages/public/PublicDonateCheckoutPage';
import PublicDonateConfirmationPage from './pages/public/PublicDonateConfirmationPage';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1a237e',
      light: '#534bae',
      dark: '#000051',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff6d00',
      light: '#ff9e40',
      dark: '#c43c00',
      contrastText: '#000000',
    },
    background: {
      default: '#f5f5f7',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#4a4a4a',
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: { fontWeight: 600, color: '#1a237e' },
    h2: { fontWeight: 500, color: '#1a237e' },
    h3: { fontWeight: 500, color: '#1a237e' },
    button: { textTransform: 'none', fontWeight: 500 },
  },
  components: {
    MuiButton: {
      defaultProps: { variant: 'contained', color: 'primary' },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 20px',
          fontWeight: 600,
          letterSpacing: 0.2,
          transition: 'all 0.15s ease-in-out',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 2px 4px rgba(0,0,0,0.2)' },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': { borderWidth: 1.5 },
        },
      },
    },
    MuiCircularProgress: {
      defaultProps: { color: 'secondary' },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': {
            transition: 'border-color 0.15s ease-in-out',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1a237e',
            borderWidth: 2,
          },
        },
        input: { backgroundColor: '#ffffff' },
      },
    },
  },
});

const rootElement = document.getElementById('root');

if (!rootElement) {
  console.error('Failed to find the root element');
} else {
  try {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <AuthProvider>
              <SessionInvalidationBridge>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/otp" element={<OtpPage />} />
                  <Route path="/donate" element={<PublicDonateLayout />}>
                    <Route index element={<PublicDonateCatalogPage />} />
                    <Route path="link" element={<PublicDonateLinkPage />} />
                    <Route path="catalog" element={<Navigate to="/donate" replace />} />
                    <Route path="cart" element={<PublicDonateCartPage />} />
                    <Route path="checkout" element={<PublicDonateCheckoutPage />} />
                    <Route path="confirmation/:orderId" element={<PublicDonateConfirmationPage />} />
                  </Route>
                  <Route element={<PrivateRoute />}>
                    <Route path="/devotees/:id" element={<PortalLayout />}>
                      <Route index element={<Navigate to="home" replace />} />
                      <Route path="welcome" element={<PortalWelcomePage />} />
                      <Route path="home" element={<PortalHomePage />} />
                      <Route path="profile" element={<PortalProfilePage />} />
                      <Route path="pledges" element={<PortalPledgesPage />} />
                      <Route path="pledges/:pledgeOrderId/pay" element={<PortalPledgePayPage />} />
                      <Route path="pledges/:pledgeOrderId/payment-receipt" element={<PortalPledgePaymentReceiptPage />} />
                      <Route path="donations" element={<PortalDonationsPage />} />
                      <Route path="statements" element={<PortalStatementsPage />} />
                    </Route>
                  </Route>
                  <Route path="/" element={<Navigate to="/donate" replace />} />
                  <Route path="*" element={<Navigate to="/donate" replace />} />
                </Routes>
              </SessionInvalidationBridge>
            </AuthProvider>
          </Router>
        </ThemeProvider>
      </React.StrictMode>,
    );
  } catch (error) {
    console.error('Error rendering React app:', error);
  }
}
